import { DiscussionContextType, Message } from "@cnx/common";
import { Box, Center, Flex, Loader, Stack } from "@mantine/core";
import ChatInput from "components/ChatInput";
import { useChatRoomSocket } from "hooks/useChatRoomSocket";
import { useDiscussion } from "hooks/useDiscussions";
import {
  useDeleteMessageMutation,
  useDeleteMessagesMutation,
  useMessages,
} from "hooks/useMessages";
import { useReadMessagesSocket } from "hooks/useReadMessagesSocket";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "stores/authStore";

import { formatChatTime } from "utils/chat";
import { showError } from "utils/index";
import { ChatSelectionHeader } from "./ui/ChatSelectionHeader";
import ChatMessageList from "./ui/ChatMessageList";
import MessageAttachment from "./ui/MessageAttachment";
import UserMessage from "./ui/UserMessage";

export const ChatFeature = ({
  discussionId,
  context,
  contextId,
  header,
  editHeader,
  popover,
  onGoBack,
  onDiscussionCreated,
  scrollMode,
}: {
  discussionId?: string;
  context?: DiscussionContextType;
  contextId?: string;
  header?: React.ReactNode;
  editHeader?: (onCancelEdit: () => void) => React.ReactNode;
  popover?: React.ReactNode;
  onGoBack?: () => void;
  onDiscussionCreated?: (discussionId: string) => void;
  scrollMode?: "window" | "container";
}) => {
  const { t } = useTranslation("messages");
  const currentUserId = useAuthStore(state => state.currentUserId);
  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState<
    string | null | undefined
  >(undefined);
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [selectedMessages, setSelectedMessages] = useState<Message[]>([]);
  const selectedMessage =
    selectedMessages.length === 1 ? selectedMessages[0] : null;

  const { mutateAsync: mutateDeleteAsync, isPending: isDeletePending } =
    useDeleteMessageMutation();
  const { mutateAsync: mutateDeleteManyAsync, isPending: isDeleteManyPending } =
    useDeleteMessagesMutation();
  const isDeleting = isDeletePending || isDeleteManyPending;

  const handleEdit = () => {
    setEditingMessage(selectedMessage);
    setSelectedMessages([]);
  };

  const handleDelete = async () => {
    if (!discussion?.id) return;
    try {
      if (selectedMessages.length === 1 && selectedMessage?.id) {
        await mutateDeleteAsync({
          id: selectedMessage.id,
          discussionId: discussion.id,
        });
      } else if (selectedMessages.length > 1) {
        const ids = selectedMessages.map(m => m.id).filter(Boolean) as string[];
        await mutateDeleteManyAsync({ ids, discussionId: discussion.id });
      }
    } catch {
      return;
    }
    setSelectedMessages([]);
  };

  const handleCopy = async () => {
    const text = [...selectedMessages]
      .sort(
        (a, b) =>
          new Date(a.createdTime ?? 0).getTime() -
          new Date(b.createdTime ?? 0).getTime()
      )
      .map(m => m.message)
      .filter(Boolean)
      .join("\n");
    if (text && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        showError(t("copyFailed"));
      }
    }
    setSelectedMessages([]);
  };

  const handleToggleSelect = (message: Message) => {
    setSelectedMessages(prev => {
      const exists = prev.some(m => m.id === message.id);
      return exists
        ? prev.filter(m => m.id !== message.id)
        : [...prev, message];
    });
  };

  const { discussion, isPending: discussionIsPending } = useDiscussion({
    discussionId,
  });
  const { messages, isPending: messagesIsLoading } = useMessages({
    discussionId,
  });

  useChatRoomSocket(discussionId);
  useReadMessagesSocket({
    messages,
    discussionId,
    context: discussion?.context,
  });

  useEffect(() => {
    if (messages.length === 0) return;
    if (firstUnreadMessageId === null) return;

    if (firstUnreadMessageId === undefined) {
      if (messages[messages.length - 1].createdBy === currentUserId) {
        setFirstUnreadMessageId(null);
        return;
      }

      const firstUnread = messages.find(
        m =>
          m.recipients?.some(
            r =>
              r.read === null ||
              (r.read === undefined && r.userId === currentUserId)
          ) && m.createdBy !== currentUserId
      );

      setFirstUnreadMessageId(firstUnread?.id ?? undefined);
    }
  }, [messages, currentUserId, firstUnreadMessageId]);

  useEffect(() => {
    if (!discussionId || discussionIsPending) return;
    if (!discussion) onGoBack?.();
  }, [discussion, discussionId, discussionIsPending, onGoBack]);

  const hasDraftContext = !discussionId && !!context && !!contextId;

  if (discussionId && !discussion && !hasDraftContext && !discussionIsPending)
    return null;
  if (!discussionId && !hasDraftContext) return null;

  return (
    <Flex
      flex={1}
      direction={"column"}
      justify={"space-between"}
      style={{ minHeight: scrollMode === "window" ? "100dvh" : 0 }}
    >
      <Stack flex={1} gap={0} style={{ minHeight: 0 }}>
        {selectedMessages.length > 0 ? (
          <ChatSelectionHeader
            selectedMessages={selectedMessages}
            onDeselect={() => setSelectedMessages([])}
            onEdit={handleEdit}
            onCopy={handleCopy}
            onDelete={handleDelete}
            isDeleting={isDeleting}
          />
        ) : editingMessage && editHeader ? (
          editHeader(() => setEditingMessage(null))
        ) : (
          header
        )}

        {popover && (
          <Center py={10} px={20}>
            <Center w={"100%"} maw={800}>
              {popover}
            </Center>
          </Center>
        )}

        <Box
          className="relative flex-1 min-h-0 flex flex-col"
          style={{ zIndex: 0 }}
        >
          {editingMessage && (
            <Box
              className="absolute inset-0 bg-black/40"
              style={{ zIndex: 45 }}
              onClick={() => setEditingMessage(null)}
            />
          )}

          <Box className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
            {messagesIsLoading &&
              !!discussionId &&
              !hasDraftContext &&
              scrollMode === "container" && (
                <Box
                  className="absolute inset-0 flex items-center justify-center bg-white/60"
                  style={{ zIndex: 30000 }}
                >
                  <Loader />
                </Box>
              )}

            {messagesIsLoading &&
            !!discussionId &&
            !hasDraftContext &&
            scrollMode !== "container" ? (
              <Center style={{ flex: 1 }} w={"100%"}>
                <Loader />
              </Center>
            ) : (
              <ChatMessageList
                messages={messages}
                discussionType={discussion?.discussionType}
                firstUnreadMessageId={firstUnreadMessageId}
                scrollMode={scrollMode}
                selectedMessageIds={
                  selectedMessages.map(m => m.id).filter(Boolean) as string[]
                }
                onSelect={handleToggleSelect}
              />
            )}
          </Box>

          <Box className="sticky bottom-0" style={{ zIndex: 50 }}>
            {editingMessage && (
              <Box className="px-2 pb-2">
                <UserMessage
                  message={editingMessage.message}
                  attachments={<MessageAttachment item={editingMessage} />}
                  time={
                    editingMessage.createdTime
                      ? formatChatTime(editingMessage.createdTime.toString())
                      : null
                  }
                  isRead={editingMessage.recipients?.some(r => r.read) ?? false}
                  isEdited={!!editingMessage.modifiedTime}
                />
              </Box>
            )}

            <ChatInput
              discussion={discussion}
              context={!discussionId ? context : undefined}
              contextId={!discussionId ? contextId : undefined}
              setFirstUnreadMessageId={setFirstUnreadMessageId}
              onDiscussionCreated={onDiscussionCreated}
              editingMessage={editingMessage}
              onCancelEdit={() => setEditingMessage(null)}
            />
          </Box>
        </Box>
      </Stack>
    </Flex>
  );
};
